import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../store/hooks';
import { fetchModuleVisibility, saveModuleVisibility } from '../api/moduleVisibilityApi';

const ModuleVisibilityContext = createContext(null);

const resolveCompany = (user) =>
  user?.company ||
  user?.custom_company ||
  user?.company_name ||
  user?.company_data?.name ||
  user?.company_data?.company_name ||
  null;

export const RESTAURANT_KEY = '__restaurant';

export const RESTAURANT_PATHS = [
  '/kds',
  '/production',
  '/production/kitchen-foods',
  '/production/factors',
  '/production/monitor',
  '/production/uom',
  '/products/recipes',
];

export const ModuleVisibilityProvider = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const company = resolveCompany(user);

  const [disabledSet, setDisabledSet] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load whenever the active company changes — guests and pre-login states stay empty.
  useEffect(() => {
    if (!isAuthenticated || !company) {
      setDisabledSet(new Set());
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchModuleVisibility(company)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.disabled_modules) ? data.disabled_modules : [];
        setDisabledSet(new Set(list));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load module visibility');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, company]);

  const isRestaurantEnabled = useMemo(
    () => !disabledSet.has(RESTAURANT_KEY),
    [disabledSet],
  );

  const save = useCallback(async (nextDisabled) => {
    if (!company) {
      throw new Error('No active company; cannot save module visibility.');
    }
    setIsSaving(true);
    setError(null);
    try {
      const data = await saveModuleVisibility({
        company,
        disabledModules: Array.from(nextDisabled),
      });
      const list = Array.isArray(data?.disabled_modules) ? data.disabled_modules : Array.from(nextDisabled);
      setDisabledSet(new Set(list));
      return list;
    } catch (err) {
      setError(err?.message || 'Failed to save module visibility');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [company]);

  const isPathDisabled = useCallback(
    (path) => (path ? disabledSet.has(path) : false),
    [disabledSet],
  );

  /**
   * Apply the company's visibility settings to a route tree of the shape used in
   * routes.js. Drops disabled children; drops top-level routes that are disabled
   * AND have no remaining visible children.
   */
  const filterByVisibility = useCallback(
    (routes) => {
      if (!routes || routes.length === 0) return [];
      if (disabledSet.size === 0) return routes;

      return routes
        .map((route) => {
          const isTopDisabled = disabledSet.has(route.path);
          let nextChildren = route.children;
          let nextPageChildren = route.pageChildren;

          if (Array.isArray(route.children)) {
            nextChildren = route.children.filter((child) => !disabledSet.has(child.path));
          }
          if (Array.isArray(route.pageChildren)) {
            nextPageChildren = route.pageChildren.filter((child) => !disabledSet.has(child.path));
          }

          const hasVisibleChild =
            (Array.isArray(nextChildren) &&
              nextChildren.some((c) => !c.hideFromMenu)) ||
            (Array.isArray(nextPageChildren) && nextPageChildren.length > 0);

          if (isTopDisabled && !hasVisibleChild) return null;

          return {
            ...route,
            children: nextChildren,
            pageChildren: nextPageChildren,
          };
        })
        .filter(Boolean);
    },
    [disabledSet],
  );

  const value = useMemo(
    () => ({
      company,
      disabled: disabledSet,
      isPathDisabled,
      filterByVisibility,
      isLoading,
      isSaving,
      error,
      save,
      isRestaurantEnabled,
    }),
    [company, disabledSet, isPathDisabled, filterByVisibility, isLoading, isSaving, error, save, isRestaurantEnabled],
  );

  return (
    <ModuleVisibilityContext.Provider value={value}>{children}</ModuleVisibilityContext.Provider>
  );
};

export const useModuleVisibility = () => {
  const ctx = useContext(ModuleVisibilityContext);
  if (!ctx) {
    // Return a no-op shape so components rendered outside the provider
    // (login screen, etc.) still work without crashing.
    return {
      company: null,
      disabled: new Set(),
      isPathDisabled: () => false,
      filterByVisibility: (routes) => routes || [],
      isLoading: false,
      isSaving: false,
      error: null,
      save: async () => {
        throw new Error('ModuleVisibilityProvider is not mounted.');
      },
      isRestaurantEnabled: true,
    };
  }
  return ctx;
};

export default ModuleVisibilityContext;
